import { DateTime } from "luxon";
import { OrganizationCountry } from "../enums/country";
import { CadIsoLocale, DeIsoLocale, Locales, UsaIsoLocale } from "../enums/locales";
import { getIsoLocaleFromCountry } from "./locales";
const DATE_ORDER_REFERENCE_DATE = new Date(Date.UTC(2023, 4, 9));
const isMonthFirstLocale = (isoLocale) => {
    const parts = new Intl.DateTimeFormat(isoLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).formatToParts(DATE_ORDER_REFERENCE_DATE);
    const monthIndex = parts.findIndex((part) => part.type === "month");
    const dayIndex = parts.findIndex((part) => part.type === "day");
    const yearIndex = parts.findIndex((part) => part.type === "year");
    return monthIndex < dayIndex && dayIndex < yearIndex;
};
export const parseDateByCountry = (date, country) => {
    return DateTime.fromFormat(date, getDateFormatByCountry(country)).toJSDate();
};
export const getDateFormatByCountry = (country) => {
    const isoLocale = country ? getIsoLocaleFromCountry(country, Locales.EN) : UsaIsoLocale.EN;
    return getDateFormatFromLocale(isoLocale);
};
export const isAmPm = (isoLocale) => {
    return new Intl.DateTimeFormat(isoLocale, { hour: "numeric" }).resolvedOptions().hour12 ?? false;
};
/**
 * Locale to pass to the date picker LocalizationProvider (adapterLocale).
 * - en-US, en-CA: week starts on Sunday.
 * - fr-CA (Canada French), en-GB, etc.: week starts on Monday (adapter uses locale convention).
 */
export const getDatePickerAdapterLocale = (isoLocale) => {
    if (isoLocale === CadIsoLocale.FR) {
        return "fr-FR";
    }
    return isoLocale;
};
/**
 * Date-only format for date picker inputs (no time), derived from Intl.
 * - United States (en-US): MM/dd/yyyy (e.g. 01/31/2021)
 * - Canada, UK, Ireland, Australia, etc.: dd/MM/yyyy (e.g. 31/01/2021)
 */
export const getDateFormatFromLocale = (isoLocale) => {
    if (isoLocale === DeIsoLocale.DE || isoLocale === DeIsoLocale.EN) {
        return "dd.MM.yyyy";
    }
    return isMonthFirstLocale(isoLocale) ? "MM/dd/yyyy" : "dd/MM/yyyy";
};
export const DATE_FIELD_PLACEHOLDER_LETTERS = {
    [Locales.EN]: { day: "D", month: "M", year: "Y" },
    [Locales.FR]: { day: "D", month: "M", year: "Y" },
    [Locales.ES]: { day: "D", month: "M", year: "Y" },
    [Locales.DE]: { day: "T", month: "M", year: "J" },
};
const DATE_HINT_LETTERS = {
    [Locales.EN]: null,
    [Locales.FR]: null,
    [Locales.ES]: null,
    [Locales.DE]: { day: "T", year: "J" },
};
export const getDateFormatHint = (format, locale) => {
    const letters = DATE_HINT_LETTERS[locale];
    if (!letters) {
        return format;
    }
    return format.replace(/d/g, letters.day).replace(/y/g, letters.year);
};
export const getLuxonFormatFromLocale = (isoLocale) => {
    const timePart = isAmPm(isoLocale) ? "hh:mm a" : "HH:mm";
    return `${getDateFormatFromLocale(isoLocale)} ${timePart}`;
};
export const getDateTime = (date, options) => {
    if (typeof date === "number") {
        return DateTime.fromMillis(date, options);
    }
    // Handle string timestamps (milliseconds)
    if (typeof date === "string" && /^\d+$/.test(date)) {
        return DateTime.fromMillis(parseInt(date, 10), options);
    }
    return DateTime.fromJSDate(new Date(date), options);
};
export const formatDateTimeToLocaleString = (date, locale, option, timeZone) => {
    if (option && locale === CadIsoLocale.FR) {
        // specific 24 time based for french
        return timeZone
            ? getDateTime(date)
                .setLocale(locale.toLowerCase())
                .setZone(timeZone)
                .toLocaleString(option ? { ...option, hour12: false } : {})
            : getDateTime(date)
                .setLocale(locale.toLowerCase())
                .toLocaleString(option ? { ...option, hour12: false } : {});
    }
    return timeZone
        ? getDateTime(date).setLocale(locale.toLowerCase()).setZone(timeZone).toLocaleString(option)
        : getDateTime(date).setLocale(locale.toLowerCase()).toLocaleString(option);
};
export const getPendingReceiptYearTurningPoint = ({ year, organizationCountry, }) => {
    const month = organizationCountry === OrganizationCountry.Australia ? 6 : 0;
    return new Date(Date.UTC(year, month, 1, 10, 0, 0));
};
export const getPendingReceiptYearBoundaries = ({ year, organizationCountry, }) => {
    if (organizationCountry === OrganizationCountry.Australia) {
        return [
            getPendingReceiptYearTurningPoint({ year: year - 1, organizationCountry }),
            getPendingReceiptYearTurningPoint({ year, organizationCountry }),
        ];
    }
    return [
        getPendingReceiptYearTurningPoint({ year, organizationCountry }),
        getPendingReceiptYearTurningPoint({ year: year + 1, organizationCountry }),
    ];
};
export const getPendingReceiptDonationDate = ({ year, organizationCountry, }) => {
    return organizationCountry === OrganizationCountry.Australia ? `${year}-06-30` : `${year}-12-30`;
};
export const getPendingReceiptYearForDate = ({ date, organizationCountry, }) => {
    const calendarYear = date.getFullYear();
    const turningPoint = getPendingReceiptYearTurningPoint({ year: calendarYear, organizationCountry });
    const australianFiscalYearShift = organizationCountry === OrganizationCountry.Australia ? 1 : 0;
    return (date.getTime() > turningPoint.getTime() ? calendarYear : calendarYear - 1) + australianFiscalYearShift;
};
